/**
 * Product gallery video: play on the first tap, and optionally as soon as the
 * slide is scrolled into view.
 *
 * Two things stand in the way of the browser doing this by itself. The video
 * is cloned out of a <template> and appended, and an `autoplay` attribute on a
 * cloned element does not reliably start playback - so playback is asked for
 * explicitly. And `loadContent` focuses the new element, which used to scroll
 * the media viewer and make the gallery pause the very video it had started.
 *
 * Everything here is muted playback, which is what browsers allow without a
 * gesture; the theme's markup already carries muted/playsinline.
 */
(() => {
  const playVideo = (media) => {
    const video = media.querySelector('video');
    if (!video) return;

    // Muted is what makes unattended playback allowed at all
    video.muted = true;
    video.setAttribute('muted', '');

    // Belt and braces for the play bar: the markup already leaves `controls`
    // off for auto-playing media, but a stale template value should not be
    // able to put a scrubber back on a video nobody is meant to operate.
    if (media.hasAttribute('data-autoplay-in-view')) {
      video.controls = false;
      video.removeAttribute('controls');
    }

    // The markup carries preload="none", so the browser holds off fetching
    // until playback is actually asked for. Waiting for loadeddata first would
    // wait forever: that event is downstream of the very call being deferred.
    // So play() goes first, and only its rejection is worth a second attempt.
    video.play().catch(() => {
      video.addEventListener('canplay', () => {
        video.play().catch(() => { /* blocked by the browser; controls remain */ });
      }, { once: true });
      video.load();
    });
  };

  /**
   * Swaps the poster for the real video ahead of time, still paused.
   *
   * Doing this at the moment the slide arrives is what the eye catches: the
   * theme fades the poster out and lifts the z-index on [loaded], so the
   * customer sees the play badge, then the swap, then playback. Preparing it
   * up front means the element is already in place, showing the same still
   * through the video's own poster attribute, and arriving on the slide only
   * starts playback.
   *
   * @param {Element} media - The deferred-media element.
   */
  const prepare = (media) => {
    if (media.getAttribute('loaded') !== null) return;

    media.loadContent(false, false, 'observer');

    const video = media.querySelector('video');
    if (!video) return;

    // The markup asks for autoplay; that decision belongs to the observer here
    video.removeAttribute('autoplay');
    video.autoplay = false;
    video.pause();

    video.muted = true;
    video.setAttribute('muted', '');
    video.controls = false;
    video.removeAttribute('controls');

    // Enough to paint a first frame without pulling the whole file down
    video.preload = 'metadata';
    video.load();
  };

  const load = (media) => {
    prepare(media);
    playVideo(media);
  };

  // A tap on the poster should be enough on its own
  document.addEventListener('click', (evt) => {
    const btn = evt.target.closest('.js-load-media');
    if (!btn) return;
    const media = btn.closest('deferred-media');
    if (!media) return;
    requestAnimationFrame(() => playVideo(media));
  });

  /**
   * How much of the element the viewport actually shows, 0 to 1.
   * @param {Element} el - Element to measure.
   * @returns {number}
   */
  const visibleFraction = (el) => {
    const rect = el.getBoundingClientRect();
    if (!rect.width || !rect.height) return 0;
    const w = Math.min(rect.right, window.innerWidth) - Math.max(rect.left, 0);
    const h = Math.min(rect.bottom, window.innerHeight) - Math.max(rect.top, 0);
    if (w <= 0 || h <= 0) return 0;
    return (w * h) / (rect.width * rect.height);
  };

  const observe = () => {
    const targets = Array.from(document.querySelectorAll('deferred-media[data-autoplay-in-view]'));
    if (!targets.length || !('IntersectionObserver' in window)) return;

    // Inserting the <video> while the gallery is still gliding puts a DOM
    // change in the middle of a scroll animation, which is what makes the
    // slide stutter on the way in. Nothing loads until the viewer has come to
    // rest, and only then if the slide is still the one being looked at.
    const settle = (media) => {
      const viewer = media.closest('.media-viewer') || media.closest('[class*="viewer"]');
      const run = () => {
        if (visibleFraction(media) > 0.6) load(media);
      };

      if (!viewer) {
        run();
        return;
      }

      clearTimeout(viewer.settleTimer);
      viewer.settleTimer = setTimeout(run, 180);

      if (!viewer.settleBound) {
        viewer.settleBound = true;
        viewer.addEventListener('scroll', () => {
          clearTimeout(viewer.settleTimer);
          viewer.settleTimer = setTimeout(() => {
            targets.forEach((el) => {
              if (visibleFraction(el) > 0.6) load(el);
            });
          }, 180);
        }, { passive: true });
      }
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        const media = entry.target;
        if (entry.isIntersecting) {
          settle(media);
        } else {
          media.querySelector('video')?.pause();
        }
      });
    }, { threshold: 0.6 });

    // Prepared as soon as the gallery itself is on screen, not per slide: a
    // slide sitting off to the side is clipped by the scroll container, and no
    // rootMargin undoes that clipping, so a per-slide observer would only fire
    // once the customer had already arrived - which is the swap being avoided.
    // Hanging it on the gallery still costs nothing on a page view that never
    // scrolls down to it.
    const galleries = new Set();
    targets.forEach((el) => {
      const gallery = el.closest('media-gallery') || el.closest('.media-gallery');
      if (gallery) galleries.add(gallery);
    });

    const prepareObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.querySelectorAll('deferred-media[data-autoplay-in-view]').forEach(prepare);
        prepareObserver.unobserve(entry.target);
      });
    });

    customElements.whenDefined('deferred-media').then(() => {
      if (galleries.size) galleries.forEach((g) => prepareObserver.observe(g));
      else targets.forEach(prepare);
    });

    targets.forEach((el) => observer.observe(el));
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', observe);
  } else {
    observe();
  }
})();
